import { useCallback } from 'react'
import { useLocalStorage } from './useLocalStorage'
import { CustomRole } from '@/types'
import { STORAGE_KEYS, DEFAULT_JURISDICTIONS, DEFAULT_CONTRACT_TYPES, DEFAULT_ROLES, DEFAULT_ROLE_INSTRUCTIONS } from '@/lib/constants'

/**
 * Hook for managing custom jurisdictions
 */
export function useCustomJurisdictions() {
  const [savedJurisdictions, setSavedJurisdictions] = useLocalStorage<string[]>(
    STORAGE_KEYS.JURISDICTIONS,
    []
  )

  const addJurisdiction = useCallback((jurisdiction: string) => {
    const trimmed = jurisdiction.trim()
    if (!trimmed) return false

    // Check if already exists in defaults or saved
    if (DEFAULT_JURISDICTIONS.includes(trimmed as any) || savedJurisdictions.includes(trimmed)) {
      return false
    }

    setSavedJurisdictions(prev => [...prev, trimmed])
    return true
  }, [savedJurisdictions, setSavedJurisdictions])

  const removeJurisdiction = useCallback((jurisdiction: string) => {
    setSavedJurisdictions(prev => prev.filter(j => j !== jurisdiction))
  }, [setSavedJurisdictions])

  const allJurisdictions = [...DEFAULT_JURISDICTIONS.slice(0, -1), ...savedJurisdictions, 'Other']

  return {
    savedJurisdictions,
    allJurisdictions,
    addJurisdiction,
    removeJurisdiction,
  }
}

/**
 * Hook for managing custom contract types
 */
export function useCustomContractTypes() {
  const [customTypes, setCustomTypes] = useLocalStorage<string[]>(
    STORAGE_KEYS.CONTRACT_TYPES,
    []
  )

  const defaultTypes = DEFAULT_CONTRACT_TYPES.map(t => t.value)

  const addContractType = useCallback((type: string) => {
    const trimmed = type.trim()
    if (!trimmed) return false

    // Check if already exists in defaults or custom
    if (defaultTypes.includes(trimmed as any) || customTypes.includes(trimmed)) {
      return false
    }

    setCustomTypes(prev => [...prev, trimmed])
    return true
  }, [customTypes, defaultTypes, setCustomTypes])

  const removeContractType = useCallback((type: string) => {
    setCustomTypes(prev => prev.filter(t => t !== type))
  }, [setCustomTypes])

  const allContractTypes = [
    ...DEFAULT_CONTRACT_TYPES.slice(0, -1),
    ...customTypes.map(t => ({ value: t, label: t })),
    { value: 'Other', label: 'Other' },
  ]

  return {
    customTypes,
    allContractTypes,
    addContractType,
    removeContractType,
  }
}

/**
 * Hook for managing custom roles
 */
export function useCustomRoles() {
  const [customRoles, setCustomRoles] = useLocalStorage<Record<string, CustomRole>>(
    STORAGE_KEYS.ROLES,
    {}
  )

  const [customRoleInstructions, setCustomRoleInstructions] = useLocalStorage<Record<string, string>>(
    STORAGE_KEYS.ROLE_INSTRUCTIONS,
    {}
  )

  const addCustomRole = useCallback((name: string, instructions: string) => {
    const trimmedName = name.trim()
    const trimmedInstructions = instructions.trim()

    if (!trimmedName || !trimmedInstructions) return null

    // Generate unique ID
    const roleId = 'custom-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9)

    const newRole: CustomRole = { name: trimmedName, instructions: trimmedInstructions }

    setCustomRoles(prev => ({ ...prev, [roleId]: newRole }))
    setCustomRoleInstructions(prev => ({ ...prev, [roleId]: trimmedInstructions }))

    return roleId
  }, [setCustomRoles, setCustomRoleInstructions])

  const updateCustomRole = useCallback((roleId: string, updates: Partial<CustomRole>) => {
    setCustomRoles(prev => ({
      ...prev,
      [roleId]: { ...prev[roleId], ...updates }
    }))
    if (updates.instructions) {
      setCustomRoleInstructions(prev => ({
        ...prev,
        [roleId]: updates.instructions!
      }))
    }
  }, [setCustomRoles, setCustomRoleInstructions])

  const deleteCustomRole = useCallback((roleId: string) => {
    if (!customRoles[roleId]) return false

    setCustomRoles(prev => {
      const { [roleId]: _, ...rest } = prev
      return rest
    })
    setCustomRoleInstructions(prev => {
      const { [roleId]: _, ...rest } = prev
      return rest
    })

    return true
  }, [customRoles, setCustomRoles, setCustomRoleInstructions])

  const getRoleName = useCallback((roleId: string): string => {
    if (DEFAULT_ROLES[roleId]) return DEFAULT_ROLES[roleId]
    if (customRoles[roleId]) return customRoles[roleId].name
    return roleId
  }, [customRoles])

  const getRoleInstructions = useCallback((roleId: string): string => {
    // Check custom roles first
    if (customRoles[roleId]) return customRoles[roleId].instructions || ''
    // Then check custom instructions on default roles
    if (customRoleInstructions[roleId]) return customRoleInstructions[roleId]
    // Finally fall back to default instructions
    return DEFAULT_ROLE_INSTRUCTIONS[roleId] || ''
  }, [customRoles, customRoleInstructions])

  const setRoleInstructions = useCallback((roleId: string, instructions: string) => {
    if (customRoles[roleId]) {
      // It's a custom role
      updateCustomRole(roleId, { instructions })
    } else {
      // It's a default role - save custom instructions
      if (instructions && instructions !== DEFAULT_ROLE_INSTRUCTIONS[roleId]) {
        setCustomRoleInstructions(prev => ({ ...prev, [roleId]: instructions }))
      } else {
        // Remove custom if it matches default or is empty
        setCustomRoleInstructions(prev => {
          const { [roleId]: _, ...rest } = prev
          return rest
        })
      }
    }
  }, [customRoles, updateCustomRole, setCustomRoleInstructions])

  const isCustomRole = useCallback((roleId: string): boolean => {
    return !DEFAULT_ROLES[roleId]
  }, [])

  const allRoles = { ...DEFAULT_ROLES }
  Object.entries(customRoles).forEach(([id, role]) => {
    allRoles[id] = role.name
  })

  return {
    customRoles,
    customRoleInstructions,
    allRoles,
    addCustomRole,
    updateCustomRole,
    deleteCustomRole,
    getRoleName,
    getRoleInstructions,
    setRoleInstructions,
    isCustomRole,
  }
}
